import React, { useState } from 'react';
import TransferLinkedCapitalActionCard, {
  CapitalActionName,
} from './TransferLinkedCapitalActionCard';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import PartnerTransferActionBar from '../../PartnerTransferActionBar';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';

interface TransferLinkedCapitalActionsContentProps {
  partnerTransferId: string;
  capitalActions: Array<LinkedCapitalAction>;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
  stepper: React.ReactNode;
}

const TransferLinkedCapitalActionsContent: React.FC<
  TransferLinkedCapitalActionsContentProps
> = ({
  partnerTransferId,
  capitalActions,
  baseCurrency,
  onNext,
  onPrevious,
  stepper,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const [isNavigating, setIsNavigating] = useState(false);
  const updatePartnerTransfer = api.partnerTransfers.update.useMutation();

  const rows = capitalActions.map((action) => ({
    ...action,
    id: action.id,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <CapitalActionName
          name={row.name}
          capitalActionHeader={row.capitalActionHeader}
        />
      ),
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'totalAmountLcy',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  const handleNext = () => {
    setIsNavigating(true);
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.LinkedCapitalActions,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionLinkedCapitalActions: true,
    };
    updatePartnerTransfer.mutate(input, {
      onSuccess: () => onNext(),
      onError: () => setIsNavigating(false),
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={handlePrevious}
          onNext={handleNext}
          isNextLoading={isNavigating}
          isNextDisabled={isNavigating}
          isPreviousDisabled={isNavigating}
        />
      }
    >
      <Table
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <TransferLinkedCapitalActionCard
            capitalAction={row}
            baseCurrency={baseCurrency}
          />
        )}
        columns={columns}
        rows={rows}
        emptyMessage="No linked capital actions found for this partner transfer."
      />
    </Page>
  );
};

export default TransferLinkedCapitalActionsContent;
